import React, { useState } from 'react';
import ReactMapGL, {
  Marker,
  Popup,
  WebMercatorViewport,
  ViewState,
} from 'react-map-gl';
import SVG from 'react-inlinesvg';
import 'mapbox-gl/dist/mapbox-gl.css';
import Star from '/images/icons/star.svg';
import {
  Wrapper,
  MarkerIcon,
  ActiveMarkerIcon,
  StyledNavigationControl,
  MarkerChild,
} from './map.style';

interface Props {
  markers?: MapMarker[];
  legend?: React.ReactNode;
  popupChild?: (props: MapMarker) => JSX.Element;
  vnmSolarProjectId?: number;
}

export const getBoundsForPoints = (points: MapMarker[]) => {
  const applyToArray = (func, array) => func.apply(Math, array);
  const pointsLong = points.map(point => point.longitude);
  const pointsLat = points.map(point => point.latitude);
  const viewport = new WebMercatorViewport({
    height: 600,
    width: 800,
  }).fitBounds(
    [
      [applyToArray(Math.min, pointsLong), applyToArray(Math.min, pointsLat)],
      [applyToArray(Math.max, pointsLong), applyToArray(Math.max, pointsLat)],
    ],
    { padding: 200 }
  );
  const { longitude, latitude, zoom } = viewport;
  return { latitude, longitude, zoom };
};

const defaultViewport = { latitude: 38, longitude: -97, zoom: 2 };

export const Map = ({
  legend,
  markers = [],
  popupChild,
  vnmSolarProjectId,
}: Props) => {
  const PopupChild = popupChild;
  const [selectedMarker, updateSelectedMarker] = useState<
    MapMarker | undefined
  >();
  const [viewport, udpateViewport] = useState<ViewState>(defaultViewport);
  if (
    markers.length &&
    JSON.stringify(viewport) === JSON.stringify(defaultViewport)
  ) {
    udpateViewport(getBoundsForPoints(markers));
  }

  const toggleSelectedMarker = marker => {
    if (selectedMarker?.id === marker.id) updateSelectedMarker(undefined);
    else updateSelectedMarker(marker);
  };

  const getIcon = marker => {
    if (marker.id === vnmSolarProjectId) {
      return <SVG src={Star} height="24px" width="24px" />;
    } else if (marker.active) {
      return <ActiveMarkerIcon />;
    } else {
      return <MarkerIcon />;
    }
  };

  return (
    <Wrapper className="notranslate" aria-hidden="true" data-testid="map">
      <ReactMapGL
        mapStyle="mapbox://styles/mapbox/light-v10"
        mapboxApiAccessToken={process.env.ARCADIA_PUBLIC_MAPBOX_ACCESS_TOKEN}
        height="300px"
        width="100%"
        aria-label="test"
        onViewportChange={viewport => {
          const { latitude, longitude, zoom } = viewport;
          udpateViewport({ latitude, longitude, zoom });
        }}
        {...viewport}
      >
        {markers.map(marker => (
          <Marker
            key={marker.id}
            latitude={marker.latitude}
            longitude={marker.longitude}
          >
            <MarkerChild onClick={() => toggleSelectedMarker(marker)}>
              {getIcon(marker)}
            </MarkerChild>
          </Marker>
        ))}
        {legend}
        {PopupChild && selectedMarker && (
          <Popup
            onClose={() => toggleSelectedMarker(selectedMarker)}
            offsetTop={8}
            offsetLeft={8}
            latitude={selectedMarker.latitude}
            longitude={selectedMarker.longitude}
          >
            <PopupChild {...selectedMarker} />
          </Popup>
        )}
        <StyledNavigationControl />
      </ReactMapGL>
    </Wrapper>
  );
};
